import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
  type TouchEvent as ReactTouchEvent,
} from 'react';
import { useOnClickOutside } from '../../../hooks/useOnClickOutside';
import { IconButton } from '../../Button/IconButton';
import { Box } from '../../Core';
import { FormControlSizes } from '../../Form/types';
import { IconName } from '../../Icons';
import { ModalContext, modalContextDefaultValue } from '../../Modal/Modal';
import {
  DragHandle,
  HeaderContainer,
  HeaderTitle,
  MOBILE_MODAL_SHEET_HEADER_HEIGHT,
  SheetContent,
  SheetModal,
  SheetTopGradient,
} from './styles';

interface MobileModalSheetProps extends PropsWithChildren {
  isOpen: boolean;
  onClose: () => void;
  /** When set, shows a header back control (e.g. pop the modal stack). */
  onBack?: () => void;
  title?: string;
  useSheetContent?: boolean;
  /** Anchors a smaller sheet to the bottom so it can sit on top of another open sheet. */
  bottomSheet?: boolean;
  bottomSheetHeight?: number | string;
  onBottomSheetExpandedChange?: (isExpanded: boolean) => void;
}

interface MobileModalSheetHeaderProps extends PropsWithChildren {
  height?: number;
}

const MOBILE_MODAL_SHEET_SELECTOR = '[data-mobile-modal-sheet]';

export function MobileModalSheetHeader({
  children,
  height = MOBILE_MODAL_SHEET_HEADER_HEIGHT,
}: MobileModalSheetHeaderProps) {
  return (
    <HeaderContainer h={height}>
      <HeaderTitle data-testid="mobile-modal-sheet-title">{children}</HeaderTitle>
    </HeaderContainer>
  );
}

export function MobileModalSheet({
  children,
  isOpen,
  onClose,
  onBack,
  title,
  useSheetContent = false,
  bottomSheet = false,
  bottomSheetHeight,
  onBottomSheetExpandedChange,
}: MobileModalSheetProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const startY = useRef(0);
  const dragOffsetRef = useRef(0);

  useEffect(() => {
    if (!isOpen) {
      setIsExpanded(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (bottomSheet) {
      onBottomSheetExpandedChange?.(isExpanded);
    }
  }, [bottomSheet, isExpanded, onBottomSheetExpandedChange]);

  let threshold = 0;
  if (typeof window !== 'undefined') {
    const screenHeight = window.innerHeight;
    threshold = screenHeight * (bottomSheet ? 0.15 : 0.5);
  }

  const handleTouchStart = useCallback(
    (e: ReactTouchEvent<HTMLDivElement>) => {
      setIsDragging(true);
      startY.current = e.touches[0].clientY;
      dragOffsetRef.current = e.touches[0].clientY;
    },
    [dragOffsetRef]
  );

  const handleTouchMove = useCallback(
    (e: ReactTouchEvent<HTMLDivElement>) => {
      if (!isDragging) {
        return;
      }

      dragOffsetRef.current = e.touches[0].clientY;
      const deltaY = dragOffsetRef.current - startY.current;

      if (deltaY > 0 || bottomSheet) {
        setDragOffset(deltaY);
      }
    },
    [bottomSheet, isDragging, dragOffsetRef]
  );

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) {
      return;
    }

    const deltaY = dragOffsetRef.current - startY.current;
    if (bottomSheet && deltaY < -threshold) {
      setIsExpanded(true);
    } else if (deltaY > threshold) {
      onClose();
    }

    startY.current = 0;
    dragOffsetRef.current = 0;
    setDragOffset(0);
    setIsDragging(false);
  }, [bottomSheet, isDragging, onClose, threshold, dragOffsetRef]);

  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!isOpen) {
        return;
      }

      const targetSheet = (event.target as Element | null)?.closest?.(MOBILE_MODAL_SHEET_SELECTOR);
      if (targetSheet != null && targetSheet !== ref.current && !bottomSheet) {
        return;
      }

      onClose();
    },
    [bottomSheet, isOpen, onClose]
  );
  useOnClickOutside(ref, handleClickOutside);

  return (
    // Lets nested popovers render above this sheet's own Z_INDEX.modal instead of behind it.
    <ModalContext.Provider value={modalContextDefaultValue}>
      <SheetModal
        isOpen={isOpen}
        dragOffset={dragOffset}
        bottomSheet={bottomSheet}
        bottomSheetHeight={bottomSheetHeight}
        isExpanded={isExpanded}
        ref={ref}
        data-testid="mobile-modal-sheet"
        data-mobile-modal-sheet=""
        data-bottom-sheet={bottomSheet ? '' : undefined}
        data-bottom-sheet-expanded={bottomSheet && isExpanded ? '' : undefined}
      >
        <DragHandle onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} />
        {useSheetContent && <SheetTopGradient hasHeader={title != null} />}
        {title != null && <MobileModalSheetHeader>{title}</MobileModalSheetHeader>}
        {useSheetContent ? (
          <SheetContent hasHeader={title != null} data-testid="mobile-modal-sheet-content">
            {children}
          </SheetContent>
        ) : (
          children
        )}
        {onBack != null && (
          <Box position="absolute" top="16px" left="16px" zIndex={10}>
            <IconButton
              round
              dim
              icon={IconName.ChevronLeft}
              aria-label="Back"
              color="gray.100"
              size={FormControlSizes.Large}
              onClick={onBack}
              ghost
              data-testid="modal-sheet-back-button"
            />
          </Box>
        )}
        <Box position="absolute" top="16px" right="16px" zIndex={10}>
          <IconButton
            round
            dim
            icon={IconName.Close}
            aria-label="Close"
            color="gray.100"
            size={FormControlSizes.Large}
            onClick={onClose}
            ghost
            data-testid="modal-sheet-close-button"
          />
        </Box>
      </SheetModal>
    </ModalContext.Provider>
  );
}
